// Pill header components (NavigationBox, SearchMobile) — ported from the customized Impact 6.6 theme.js bundle.
// SearchMobile extends the theme's dialog-element base class, resolved at runtime from the 7.2 bundle.
// This file owns visible-viewport sizing and overlay handoffs. Menu, search and
// cart must close/settle before the incoming panel is measured; measuring a
// still-stacked panel recreates the miniature-drawer bug. See AGENTS.md.
import { timeline as motionTimeline, stagger } from 'vendor';

// Respect the user's reduced-motion preference (vanilla 7.2 does the same):
// when reduced, jump straight to each animation's final state.
function timeline(sequence, options) {
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		sequence.forEach((item) => {
			if (!Array.isArray(item)) return;
			const [target, keyframes] = item;
			const elements = target instanceof Element ? [target] : Array.from(target);
			elements.forEach((el) => {
				for (const prop in keyframes) {
					const value = keyframes[prop];
					el.style[prop] = Array.isArray(value) ? value[value.length - 1] : value;
				}
			});
		});
		return { finished: Promise.resolve() };
	}
	return motionTimeline(sequence, options);
}

function debounce(fn, delay) {
	let timer = null;
	return (...args) => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			fn.apply(this, args);
		}, delay);
	};
}

// Expanded header panels are overlays, not additional page content. Record
// the closed pill's footprint once it has rendered so menu/search/cart can
// grow over the locked page without ResizeObserver pushing that page down.
// Measure the header CONTENT (store-header), never the section: below 1150px
// the section's height IS this lock (custom.css), so measuring it would only
// read back whatever was captured last and a wrong value could never heal.
// A closed drawer that still has height is mid-transition (crossing the
// desktop breakpoint animates the menu/search closed); measure again once
// that has settled instead of locking a frame of the animation.
function captureRestingHeaderHeight(storeHeader) {
	if (!storeHeader || storeHeader.hasAttribute('expanded') || !window.matchMedia('(max-width: 1149px)').matches) return;
	const section = storeHeader.closest('.shopify-section--header-pill');
	if (!section) return;
	const settling = Array.from(storeHeader.querySelectorAll('navigation-box[closed], .header__search-drawer-mobile[closed]')).some(
		(drawer) => drawer.getBoundingClientRect().height > 0
	);
	if (settling) {
		clearTimeout(captureRestingHeaderHeight._retry);
		captureRestingHeaderHeight._retry = setTimeout(() => captureRestingHeaderHeight(storeHeader), 450);
		return;
	}
	const height = Math.round(storeHeader.getBoundingClientRect().height);
	if (height >= 60) section.style.setProperty('--bs-header-resting-height', `${height}px`);
}

// Crossing the 1150px breakpoint from desktop applies the drawers' [closed]
// rules, whose 400ms height transition then runs from their desktop height
// down to 0 — invisible (visibility: hidden) but 1,400px of layout that the
// header height-observer reported into --header-height and the homepage hero
// grew by. A closed drawer has nothing to animate, so while the pill is not
// expanded any viewport resize snaps them shut (component-header-pill.css
// [data-bs-settling]) for two frames. Skipped while a drawer is open so the
// mobile address-bar resize cannot snap a running open/close animation.
function snapClosedDrawersOnResize(storeHeader) {
	if (!storeHeader) return;
	let release = 0;
	window.addEventListener('resize', () => {
		if (storeHeader.hasAttribute('expanded')) return;
		storeHeader.setAttribute('data-bs-settling', '');
		cancelAnimationFrame(release);
		release = requestAnimationFrame(() => {
			release = requestAnimationFrame(() => storeHeader.removeAttribute('data-bs-settling'));
		});
	});
}

// Desktop mega-menu bodies ship as <template data-bs-menu-lazy> (sections/header-pill.liquid)
// and are materialised once, on the first interaction with the nav — pointer, touch or keyboard
// — in the capture phase, so the content exists before the vendor disclosure opens.
(() => {
	const hydrate = () => {
		document.querySelectorAll('template[data-bs-menu-lazy]').forEach((template) => {
			template.parentNode?.insertBefore(template.content.cloneNode(true), template);
			template.remove();
		});
	};
	const arm = () => {
		const nav = document.querySelector('store-header .header__link-list');
		if (!nav || !nav.querySelector('template[data-bs-menu-lazy]')) return;
		const once = { capture: true, once: true, passive: true };
		['pointerenter', 'pointerdown', 'touchstart', 'focusin'].forEach((name) => nav.addEventListener(name, hydrate, once));
		// Fallback for any path that skips those events (e.g. programmatic open).
		document.addEventListener('shopify:section:load', hydrate, { once: true });
	};
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm);
	else arm();
})();

var NavigationBox = class extends HTMLElement {
	static get observedAttributes() {
		return ['open'];
	}

	constructor() {
		super();
		this._isTransitioning = false;
		this._open = this.hasAttribute('open');
		this.setAttribute('aria-expanded', this._open ? 'true' : 'false');

		this.addEventListener('click', (e) => {
			if ((e.target.hasAttribute('data-panel') && e.target.nodeName === 'BUTTON') || e.target.closest('button[data-panel]')) {
				const goodTarget = e.target.hasAttribute('data-panel') ? e.target : e.target.closest('button[data-panel]');

				if (goodTarget) {
					this._onPanelButtonClick(null, goodTarget);
				}
			}
		});

		this.storeHeader = this.closest('store-header');
		snapClosedDrawersOnResize(this.storeHeader);
		requestAnimationFrame(() => requestAnimationFrame(() => captureRestingHeaderHeight(this.storeHeader)));
		this.summaryElement = this.storeHeader.querySelector('button[aria-controls="header-menu"]');
		this.contentElement = this;
		this.menuOverlay = this.storeHeader.querySelector('div[part="overlay"]');
		this.menuSearchMobile = this.storeHeader.querySelector('search-mobile');
		this.promoSlider = this.querySelector('.secondary-menu__scroller');
		this.deviceSheet = this.querySelector('[data-menu-device-sheet]');
		this.deviceOptions = this.querySelector('[data-menu-device-options]');
		this.deviceModels = this.querySelector('[data-menu-device-models]');
		this.deviceTabs = this.querySelector('[data-menu-device-tabs]');
		this._setupDeviceSelector();
		this.checkPromoSliderButtons();
		this.summaryElement.addEventListener('click', this._onSummaryClicked.bind(this));
		this.menuOverlay.addEventListener('click', this._onMenuOverlayClicked.bind(this));
		this._onViewportResize = debounce(() => {
			captureRestingHeaderHeight(this.storeHeader);
			this.checkWidthAndCloseMenu();
			this.checkPromoSliderButtons();
			if (this.open) this._sizeToViewport();
		}, 100);
		this._onKeyDown = this._handleKeyDown.bind(this);
		this.addEventListener('keydown', this._onKeyDown);
		window.addEventListener('resize', this._onViewportResize);
		window.visualViewport?.addEventListener('resize', this._onViewportResize);
		this.onSummaryClicked = this._onSummaryClicked.bind(this);
	}

	disconnectedCallback() {
		this.removeEventListener('keydown', this._onKeyDown);
		window.removeEventListener('resize', this._onViewportResize);
		window.visualViewport?.removeEventListener('resize', this._onViewportResize);
		window.removeEventListener('blancspace:phone-model-change', this._onDevicePreferenceChange);
	}

	_handleKeyDown(event) {
		if (event.key === 'Escape') {
			event.preventDefault();
			if (this.deviceSheet && !this.deviceSheet.hidden) {
				this._hideDeviceSelector();
				return;
			}
			this.open = false;
			this.summaryElement?.removeAttribute('open');
			this.storeHeader?.removeAttribute('expanded');
			this.summaryElement?.focus({ preventScroll: true });
			return;
		}

		if (event.key !== 'Tab' || !this.open) return;
		const scope = this.deviceSheet && !this.deviceSheet.hidden ? this.deviceSheet : this;
		const focusable = Array.from(scope.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'))
			.filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length);
		if (!focusable.length) return;

		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	}

	_setupDeviceSelector() {
		if (!this.deviceSheet || !this.deviceOptions || !this.deviceModels || !this.deviceTabs) return;
		this.deviceEntries = Array.from(this.deviceOptions.options).map((option) => ({
			model: option.value,
			brand: option.dataset.phoneBrand || 'other',
			url: option.dataset.collectionUrl || '',
			source: option.dataset.phoneModelSource || 'legacy',
		})).filter((entry) => entry.model);
		// Exact collection metafields are authoritative during migration. Sort
		// them before legacy redirect-derived entries so duplicate model labels
		// keep the clean, model-specific collection route. For duplicate legacy
		// entries, prefer the URL containing the most exact model-name tokens.
		const routeScore = (entry) => entry.model.toLowerCase().match(/[a-z0-9]+/g)?.filter((token) => entry.url.toLowerCase().includes(token)).length || 0;
		this.deviceEntries.sort((a, b) => {
			return (a.source === 'metafield' ? 0 : 1) - (b.source === 'metafield' ? 0 : 1) || routeScore(b) - routeScore(a);
		});
		const seen = new Set();
		this.deviceEntries = this.deviceEntries.filter((entry) => {
			const key = entry.model.toLowerCase();
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		}).sort((a, b) => {
			const generation = (model) => {
				if (/^iPhone Air$/i.test(model)) return 17;
				return Number(model.match(/(?:iPhone\s+|Pixel\s+|\bS)(\d+)/i)?.[1] || 0);
			};
			const tier = (model) => /pro max|ultra/i.test(model) ? 0 : /\bpro\b/i.test(model) ? 1 : /\bair\b|\bplus\b/i.test(model) ? 2 : /\bflip\b|\bfold\b/i.test(model) ? 3 : 4;
			return generation(b.model) - generation(a.model) || tier(a.model) - tier(b.model) || a.model.localeCompare(b.model);
		});

		this.querySelector('[data-menu-device-open]')?.addEventListener('click', () => this._showDeviceSelector());
		this.querySelector('[data-menu-device-close]')?.addEventListener('click', () => this._hideDeviceSelector());
		this._onDevicePreferenceChange = (event) => this._updateDeviceLabel(event.detail?.model || '');
		window.addEventListener('blancspace:phone-model-change', this._onDevicePreferenceChange);
		let storedModel = window.BlancSpaceDevicePreference?.get() || '';
		if (!storedModel) try { storedModel = localStorage.getItem('blancspace.phoneModel') || ''; } catch (error) {}
		this._updateDeviceLabel(storedModel);
	}

	async _showDeviceSelector() {
		await window.BlancSpaceDevicePreference?.visualReady;
		const brandOrder = ['apple', 'samsung', 'google', 'other'];
		const availableBrands = new Set(this.deviceEntries.map((entry) => entry.brand));
		const brands = brandOrder.filter((brand) => availableBrands.has(brand));
		brands.push(...[...availableBrands].filter((brand) => !brandOrder.includes(brand)).sort());
		let storedModel = window.BlancSpaceDevicePreference?.get() || '';
		if (!storedModel) try { storedModel = localStorage.getItem('blancspace.phoneModel') || ''; } catch (error) {}
		const storedBrand = this.deviceEntries.find((entry) => entry.model === storedModel)?.brand;
		const detectedBrand = window.BlancSpaceDevicePreference?.getDetectedBrand?.();
		const preferredBrand = storedBrand || detectedBrand || 'apple';
		const activeBrand = brands.includes(preferredBrand) ? preferredBrand : brands[0];
		this.deviceTabs.replaceChildren(...brands.map((brand) => {
			const button = document.createElement('button');
			button.type = 'button';
			button.className = 'menu-device-selector__tab';
			button.dataset.deviceBrand = brand;
			button.textContent = ({ apple: 'Apple', samsung: 'Samsung', google: 'Google', other: 'Other' })[brand] || brand;
			button.setAttribute('role', 'tab');
			button.setAttribute('aria-selected', brand === activeBrand ? 'true' : 'false');
			button.addEventListener('click', () => this._renderDeviceModels(brand));
			return button;
		}));
		this._renderDeviceModels(activeBrand);
		this.deviceSheet.hidden = false;
		this.deviceSheet.setAttribute('aria-hidden', 'false');
		this.querySelector('[data-menu-device-open]')?.setAttribute('aria-expanded', 'true');
		this.deviceSheet.querySelector('[data-menu-device-close]')?.focus({ preventScroll: true });
	}

	_hideDeviceSelector() {
		this.deviceSheet.hidden = true;
		this.deviceSheet.setAttribute('aria-hidden', 'true');
		this.querySelector('[data-menu-device-open]')?.setAttribute('aria-expanded', 'false');
		this.querySelector('[data-menu-device-open]')?.focus({ preventScroll: true });
	}

	_renderDeviceModels(brand) {
		this.deviceTabs.querySelectorAll('[role="tab"]').forEach((tab) => tab.setAttribute('aria-selected', tab.dataset.deviceBrand === brand ? 'true' : 'false'));
		let storedModel = window.BlancSpaceDevicePreference?.get() || '';
		if (!storedModel) try { storedModel = localStorage.getItem('blancspace.phoneModel') || ''; } catch (error) {}
		this.deviceModels.replaceChildren(...this.deviceEntries.filter((entry) => entry.brand === brand).map((entry) => {
			const button = document.createElement('button');
			button.type = 'button';
			button.className = 'menu-device-selector__model';
			button.textContent = entry.model;
			button.setAttribute('aria-current', entry.model === storedModel ? 'true' : 'false');
			button.addEventListener('click', async () => {
				button.disabled = true;
				// A pick with a collection URL navigates at once. set() writes
				// localStorage synchronously and mirrors the cart with a keepalive POST
				// that completes during the navigation; the destination is that model's
				// own locked collection, so its first render never depends on the cart
				// attribute. Awaiting the POST here added its round trip (~0.4 s from a
				// fast line, more on a phone) to a tap that already waits for a page load
				// (Scott, 12 Oct 2026: "a good 5 seconds" from the Shop by Phone menu).
				const preference = window.BlancSpaceDevicePreference;
				const saved = preference ? preference.set(entry.model, { source: 'menu', force: true }) : Promise.resolve();
				if (!preference) {
					try { localStorage.setItem('blancspace.phoneModel', entry.model); } catch (error) {}
					window.dispatchEvent(new CustomEvent('blancspace:phone-model-change', { detail: { model: entry.model, source: 'menu', locked: true } }));
				}
				if (entry.url) {
					window.location.assign(entry.url);
					return;
				}
				try { await saved; } catch (error) { /* the local write already happened */ }
				button.disabled = false;
				this._hideDeviceSelector();
			});
			return button;
		}));
	}

	_updateDeviceLabel(model) {
		this.querySelectorAll('[data-menu-device-prompt]').forEach((prompt) => {
			prompt.textContent = model ? 'Selected phone' : 'Select your phone';
		});
		this.querySelectorAll('[data-menu-device-label]').forEach((label) => {
			label.textContent = model || 'Choose model';
		});
	}

	set open(value) {
		if (value !== this._open) {
			this._open = value;
			this.summaryElement?.setAttribute('aria-expanded', value ? 'true' : 'false');
			if (this.isConnected) {
				this._transition(value);
			} else {
				value ? this.setAttribute('open', '') : this.removeAttribute('open');
			}
		}
	}
	get open() {
		return this._open;
	}
	_onSummaryClicked(event) {
		event ? event.preventDefault() : null;
		// A second tap inside the 400ms slide used to toggle the menu straight
		// back (PostHog: open → closed within 250-400ms, then a third tap). Treat
		// taps during the transition as the same intent.
		const now = performance.now();
		if (event && now - (this._lastToggleAt || 0) < 400) return;
		this._lastToggleAt = now;
		this.open = !this.open;

		if (this.open) {
			this.summaryElement.setAttribute('open', '');
			this.storeHeader.setAttribute('expanded', '');
		} else {
			this.summaryElement.removeAttribute('open');
			this.storeHeader.removeAttribute('expanded');
		}
	}

	_onMenuOverlayClicked(event) {
		// Only tear down the pill when the menu itself owns it — the cart,
		// wishlist and search close themselves from the same dimmer click.
		if (event.target.getAttribute('part') === 'overlay' && this.open) {
			this.open = false;
			this.summaryElement.removeAttribute('open');
			this.storeHeader.removeAttribute('expanded');
		}
	}
	async _transition(value) {
		this.style.overflow = 'hidden';
		if (value) {
			this.menuSearchMobile && this.menuSearchMobile.hasAttribute('open') ? this.menuSearchMobile.hide() : null;
			this._sizeToViewport();
			// Re-check once the menu's close transition has settled. The first
			// stable-row measurement prevents the tiny panel; this second pass
			// accounts for mobile browser chrome moving during the handoff.
			requestAnimationFrame(() => requestAnimationFrame(() => this._sizeToViewport()));
			setTimeout(() => this.hasAttribute('open') && this._sizeToViewport(), 450);
			this.removeAttribute('closed');
			this.setAttribute('open', '');
			document.body.classList.add('overflow-hidden');
			this.menuOverlay.removeAttribute('hidden');
			setTimeout(() => {
				if (!this.open) return;
				Array.from(this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])'))
					.find((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length)
					?.focus({ preventScroll: true });
			}, 350);
		} else {
			this.removeAttribute('open');
			this.setAttribute('closed', '');
			document.body.classList.remove('overflow-hidden');
			this.menuOverlay.setAttribute('hidden', '');
		}
	}
	attributeChangedCallback(name, oldValue, newValue) {
		if (name === 'open') {
			this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
		}
	}

	_sizeToViewport() {
		// CSS vh can include Chrome/Safari's browser UI. Measure the visible
		// viewport so the final menu controls remain above that UI and tappable.
		const PILL_INSET = 34;
		const top = this.getBoundingClientRect().top;
		const available = Math.max(0, Math.round(window.innerHeight - top - PILL_INSET));
		this.style.setProperty('--bs-menu-h', available + 'px');
	}

	checkWidthAndCloseMenu() {
		if (window.matchMedia('(min-width: 1150px)').matches && this.hasAttribute('open')) {
			this.open = false;
			this.summaryElement.removeAttribute('open');
			this.storeHeader.removeAttribute('expanded');
			document.body.classList.remove('overflow-hidden');
			this.menuOverlay.setAttribute('hidden', '');
		}
	}

	// Used for navigation mobile and navigation desktop set to drawer
	switchToPanel(panelIndex, linkListIndex = null) {
		const panels = this.querySelectorAll('.panel');
		let panelToHideTransform,
			panelToShowTransform,
			panelToHide = linkListIndex !== null ? panels[parseInt(panelIndex) - 1] : panels[parseInt(panelIndex) + 1],
			panelToShow = panels[panelIndex],
			linkLists = panelToShow.querySelectorAll('.panel__wrapper'),
			timelineSequence = [];
		if (document.dir === 'rtl') {
			panelToHideTransform = linkListIndex !== null ? ['translateX(0%)', 'translateX(100%)'] : ['translateX(0%)', 'translateX(-100%)'];
			panelToShowTransform = linkListIndex !== null ? ['translateX(-100%)', 'translateX(0%)'] : ['translateX(100%)', 'translateX(0%)'];
		} else {
			panelToHideTransform = linkListIndex !== null ? ['translateX(0%)', 'translateX(-100%)'] : ['translateX(0%)', 'translateX(100%)'];
			panelToShowTransform = linkListIndex !== null ? ['translateX(100%)', 'translateX(0%)'] : ['translateX(-100%)', 'translateX(0%)'];
		}
		timelineSequence.push(
			[
				panelToHide,
				{ transform: panelToHideTransform, opacity: [1, 0], visibility: ['visible', 'hidden'] },
				{ duration: 0.3, opacity: { at: '+0.3' }, transform: { at: '+0.3' } },
			],
			'panelHidden',
			[
				panelToShow,
				{ opacity: [0, 1], visibility: ['hidden', 'visible'], transform: panelToShowTransform },
				{ at: '<', transform: { duration: 0.3 } },
			]
		);
		if (linkListIndex !== null) {
			timelineSequence.push(this.switchLinkList(linkLists, linkListIndex));
		}
		timeline(timelineSequence);
	}
	// Used when mega menu is set to drawer
	showPanel(panelIndex, linkListIndex = null) {
		const panels = this.querySelectorAll('.panel');
		let timelineSequence = [],
			panelToShow = panels[panelIndex],
			linkLists = panelToShow.querySelectorAll('.panel__wrapper');
		if (!panelToShow.hasAttribute('open') && !this._isTransitioning) {
			this._isTransitioning = true;
			timelineSequence.push(
				[
					this,
					{
						width: [
							this.offsetWidth + 'px',
							(this.offsetWidth - parseInt(window.getComputedStyle(this).getPropertyValue('padding'))) * 2 + 'px',
						],
					},
					{ duration: 0.2 },
				],
				[panelToShow, { opacity: [0, 1], visibility: ['hidden', 'visible'] }, { at: '<' }]
			);
			timelineSequence.push(this.switchLinkList(linkLists, linkListIndex));
		}
		if (this.previousLinkIndex && this.previousLinkIndex !== linkListIndex) {
			timelineSequence.push([linkLists[this.previousLinkIndex], { opacity: [1, 0], visibility: ['visible', 'hidden'] }, { duration: 0.2 }]);
		}
		// A cancelled timeline REJECTS .finished; unhandled, that strands
		// _isTransitioning=true and freezes panel switching — always settle.
		timeline(timelineSequence).finished.catch(() => {}).then(() => {
			if (this.previousLinkIndex && this.previousLinkIndex !== linkListIndex) {
				linkLists[this.previousLinkIndex].removeAttribute('style');
			}
			if (panelToShow.hasAttribute('open') && this.previousLinkIndex !== linkListIndex) {
				timeline([this.switchLinkList(linkLists, linkListIndex)]);
			}
			this.previousLinkIndex = linkListIndex;
			panelToShow.setAttribute('open', '');
			this._isTransitioning = false;
		});
	}
	switchLinkList(linkLists, linkListIndex) {
		Array.from(linkLists).forEach((item) => {
			item.setAttribute('hidden', '');
		});
		linkLists[linkListIndex].removeAttribute('hidden');
		return [
			linkLists[linkListIndex].querySelectorAll('li'),
			{ opacity: [0, 1], visibility: ['hidden', 'visible'], transform: ['translateY(-10px)', 'translateY(0)'] },
			{ easing: 'ease', duration: 0.2, at: '-0.15', delay: stagger(0.025, { start: 0.1 }) },
		];
	}
	setExpanded(target) {
		Array.from(this.querySelectorAll('button[data-panel]')).forEach((item) => {
			if (item.hasAttribute('aria-expanded')) item.setAttribute('aria-expanded', 'false');
		});
		if (target?.hasAttribute('aria-expanded')) {
			target.setAttribute('aria-expanded', 'true');
		}
	}
	_onPanelButtonClick(event, target) {
		this.setExpanded(target);
		this.switchToPanel(...target.getAttribute('data-panel').split('-'));
	}

	checkPromoSliderButtons() {
		if (this.promoSlider) {
			const buttons = this.promoSlider.parentElement.querySelectorAll('button');
			if (buttons[0] && window.matchMedia('(min-width: 700px)').matches) {
				if (buttons[0].offsetLeft > 0) {
					buttons.forEach((button) => {
						button.setAttribute('hidden', '');
					});
				} else {
					buttons.forEach((button) => {
						button.removeAttribute('hidden');
					});
				}
			} else {
				buttons.forEach((button) => {
					button.removeAttribute('hidden');
				});
			}
		}
	}
};

if (!window.customElements.get('navigation-box')) {
	window.customElements.define('navigation-box', NavigationBox);
}

// The resting-height lock must exist before any pill overlay expands (the
// height-observer and PDP info row both depend on it), so capture it here
// independently of navigation-box (which only renders when menus exist) and
// re-capture once webfonts settle in case the first measurement ran early.
const bootstrapRestingHeaderHeight = () => {
	const storeHeader = document.querySelector('.shopify-section--header-pill store-header');
	if (!storeHeader) return;
	requestAnimationFrame(() => requestAnimationFrame(() => captureRestingHeaderHeight(storeHeader)));
	document.fonts?.ready?.then(() => captureRestingHeaderHeight(storeHeader));
};
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', bootstrapRestingHeaderHeight);
} else {
	bootstrapRestingHeaderHeight();
}

customElements.whenDefined('dialog-element').then(() => {
	const DialogElement = customElements.get('dialog-element');

	var SearchMobile = class extends DialogElement {
	constructor() {
		super();
		this.storeHeader = this.closest('store-header');
		this.menuOverlay = this.storeHeader.querySelector('div[part="overlay"]');
		this._onOverlayClick = this._onMenuOverlayClicked.bind(this);
		this.menuOverlay.addEventListener('click', this._onOverlayClick);
		this.navigationBox = this.storeHeader.querySelector('navigation-box');
		this._onViewportResize = debounce(() => {
			this.checkWidthAndCloseMenu();
			if (this.open) this._sizeToViewport();
		}, 250);
		window.addEventListener('resize', this._onViewportResize);
		window.visualViewport?.addEventListener('resize', this._onViewportResize);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.menuOverlay?.removeEventListener('click', this._onOverlayClick);
		window.removeEventListener('resize', this._onViewportResize);
		window.visualViewport?.removeEventListener('resize', this._onViewportResize);
	}

	attributeChangedCallback(name, oldValue, newValue) {
		// Preserve Impact 7.2's DialogElement lifecycle: focus trapping, inert
		// state, aria-expanded controls, toggle events and after-show/hide events.
		// The pill-specific sizing and sibling hand-off extend that behaviour.
		super.attributeChangedCallback(name, oldValue, newValue);
		if (name !== 'open') return;

		if (this.hasAttribute('open')) {
			this.navigationBox && this.navigationBox.open ? this.navigationBox.onSummaryClicked() : null;
			const cartDrawer = this.storeHeader.querySelector('#cart-drawer');
			if (cartDrawer?.open && typeof cartDrawer.hide === 'function') cartDrawer.hide();
			this._sizeToViewport();
			this.menuOverlay.removeAttribute('hidden');
			document.body.classList.add('overflow-hidden');
			this.storeHeader.setAttribute('expanded', '');
			// The panel is still visibility:hidden on the first frame (400ms
			// transition), so an rAF focus was refused and typing went nowhere;
			// retry once the transition has revealed the input.
			const focusInput = () => {
				const input = this.querySelector('[data-search-input]');
				if (input && document.activeElement !== input) input.focus({ preventScroll: true });
			};
			requestAnimationFrame(focusInput);
			setTimeout(focusInput, 450);
		} else {
			// Don't strip the pill state a sibling drawer still owns (opening the
			// cart/wishlist closes the search first — this runs mid-handoff).
			const cartDrawer = this.storeHeader.querySelector('#cart-drawer');
			const wishlistDrawer = this.storeHeader.querySelector('#wishlist-drawer');
			const siblingOwnsPill = cartDrawer?.hasAttribute('open') || wishlistDrawer?.hasAttribute('open');
			if (!siblingOwnsPill) {
				this.menuOverlay.setAttribute('hidden', '');
				this.storeHeader.removeAttribute('expanded');
			}
			document.body.classList.remove('overflow-hidden');
		}
	}

	_sizeToViewport() {
		// Search and navigation occupy the same grid row. During a handoff the
		// navigation panel is still animating closed, so the header's bottom is
		// temporarily near the viewport bottom and would yield a tiny search.
		// Measure from the shared row's stable top, as the pill cart does.
		const rowTop = this.navigationBox?.getBoundingClientRect().top ?? this.getBoundingClientRect().top;
		const bottomInset = 34;
		this.style.setProperty('--bs-search-h', `${Math.max(320, window.innerHeight - rowTop - bottomInset)}px`);
	}

	_onMenuOverlayClicked(event) {
		if (event.target.getAttribute('part') === 'overlay' && this.hasAttribute('open')) {
			this.hide();
			this.storeHeader.removeAttribute('expanded');
		}
	}

	checkWidthAndCloseMenu() {
		if (window.matchMedia('(min-width: 700px)').matches && this.hasAttribute('open')) {
			this.hide();
			document.body.classList.remove('overflow-hidden');
			this.menuOverlay.setAttribute('hidden', '');
		}
	}
};


	if (!window.customElements.get('search-mobile')) {
		window.customElements.define('search-mobile', SearchMobile);
	}
});
